import React, { useEffect, useState } from 'react';
import RootLayout from 'layouts/RootLayout';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { liff } from '@line/liff';
import { useTranslation } from 'react-i18next';

const LIFF_ID = process.env.REACT_APP_CLINIC_LIFF_ID;
const backendEventUrl = process.env.REACT_APP_BACKEND_EVENT_URL;

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '0px solid #000',
  boxShadow: 24,
  p: 1,
};

const initializeLIFF = async () => {
  try {
    await liff.init({ liffId: LIFF_ID });

    if (!liff.isLoggedIn()) {
      liff.login();
    }

    return liff;
  } catch (error: unknown) {
    console.error('初始化 liff 時出錯：', error);
  }
};

const SetClinic = () => {
  const { t } = useTranslation("clinic");
  const [liffClientObject, setLiffClientObject] = useState(null);
  const [serviceTimes, setServiceTimes] = useState([
    Array(7).fill({ yn: 'N', tagw: '', urlYN: 'X', hospName: '' }),
    Array(7).fill({ yn: 'N', tagw: '', urlYN: 'X', hospName: '' }),
    Array(7).fill({ yn: 'N', tagw: '', urlYN: 'X', hospName: '' }),
  ]);
  const [notes, setNotes] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      const liffObject = await initializeLIFF();
      setLiffClientObject(liffObject);

      if (liffObject) {
        const profile = await liffObject.getProfile();
        const accessToken = liffObject.getAccessToken();
        const lineId = profile.userId;

        const response = await fetch(`${backendEventUrl}/facility/getFacilityByLineId`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lineId: lineId,
            accessToken: accessToken,
          }),
        });

        if (!response.ok) {
          setError(true);
          setLoading(false);
          return;
        }

        const data = await response.json();
        if (!data.name) {
          setError(true);
          setLoading(false);
          return;
        }

        setClinicName(data.name);
        if (data.serviceInfo) {
          setServiceTimes(data.serviceInfo.srv_Time);
          setNotes(data.serviceInfo.srv_Memo);
        }
      }
      setLoading(false);
    };
    initialize();
  }, []);

  const handleServiceTimeChange = (periodIndex: number, dayIndex: number) => {
    setServiceTimes(prev => {
      const newServiceTimes = [...prev];
      newServiceTimes[periodIndex][dayIndex] = {
        ...newServiceTimes[periodIndex][dayIndex],
        yn: newServiceTimes[periodIndex][dayIndex].yn === 'Y' ? 'N' : 'Y',
      };
      return newServiceTimes;
    });
  };

  const handleSubmit = async () => {
    try {
      const profile = await liffClientObject.getProfile();
      const accessToken = liffClientObject.getAccessToken();
      const lineId = profile.userId;

      const existingResponse = await fetch(`${backendEventUrl}/facility/getFacilityByLineId`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lineId: lineId,
          accessToken: accessToken,
        }),
      });

      if (!existingResponse.ok) {
        throw new Error('Failed to fetch existing service information');
      }

      const existingData = await existingResponse.json();
      const existingServiceInfo = existingData.serviceInfo;

      const updatedNotes = notes?.trim() === '' ? null : notes;

      const updatedServiceInfo = {
        ...existingServiceInfo,
        srv_Time: serviceTimes,
        srv_Memo: updatedNotes,
      };

      const response = await fetch(`${backendEventUrl}/facility/updateServiceInfo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lineId: lineId,
          accessToken: accessToken,
          serviceInfo: updatedServiceInfo,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update service information');
      }

      const data = await response.json();
      console.log('Update successful:', data);
      setMessage({ type: 'success', text: t('set.saveSuccess') });
    } catch (error) {
      console.error('Error updating service information:', error);
      setMessage({ type: 'error', text: t('set.saveError') });
    }
  };

  const handleClose = () => {
    liff.closeWindow();
  };

  return (
    <RootLayout title={t('set.title')}>
      <Box sx={style}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {t('set.readError')}
            </Typography>
            <Button onClick={handleClose} variant="contained" color="secondary" fullWidth>
              {t('common:action.close')}
            </Button>
          </>
        ) : (
          <>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {t('set.heading')}
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 1 }}>
              {t('set.facilityNameLabel', { name: clinicName })}
            </Typography>
            {message && (
              <Alert severity={message.type} sx={{ mb: 2 }}>
                {message.text}
              </Alert>
            )}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ padding: '1px', fontSize: '18px', backgroundColor: '#FCE4EC80' }}
                               align="center">{t("schedule.weekdayHeader")}</TableCell>
                    <TableCell style={{ padding: '1px', fontSize: '18px', backgroundColor: '#FCE4EC80' }}
                               align="center">{t("schedule.mon")}</TableCell>
                    <TableCell style={{ padding: '1px', fontSize: '18px', backgroundColor: '#FCE4EC80' }}
                               align="center">{t("schedule.tue")}</TableCell>
                    <TableCell style={{ padding: '1px', fontSize: '18px', backgroundColor: '#FCE4EC80' }}
                               align="center">{t("schedule.wed")}</TableCell>
                    <TableCell style={{ padding: '1px', fontSize: '18px', backgroundColor: '#FCE4EC80' }}
                               align="center">{t("schedule.thu")}</TableCell>
                    <TableCell style={{ padding: '1px', fontSize: '18px', backgroundColor: '#FCE4EC80' }}
                               align="center">{t("schedule.fri")}</TableCell>
                    <TableCell style={{ padding: '1px', fontSize: '18px', backgroundColor: '#FCE4EC80' }}
                               align="center">{t("schedule.sat")}</TableCell>
                    <TableCell style={{ padding: '1px', fontSize: '18px', backgroundColor: '#FCE4EC80' }}
                               align="center">{t("schedule.sun")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['morning', 'afternoon', 'evening'].map((period, periodIndex) => (
                    <TableRow key={period}
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell style={{ padding: '1px', paddingTop: '5px', paddingBottom: '5px', fontSize: '18px' }}
                                 align="center">
                        {periodIndex === 0 ? t("schedule.morning") : periodIndex === 1 ? t("schedule.afternoon") : t("schedule.evening")}
                      </TableCell>
                      {serviceTimes[periodIndex].map((time, dayIndex) => (
                        <TableCell style={{
                          padding: '1px',
                          paddingTop: '5px',
                          paddingBottom: '5px',
                          fontSize: '18px',
                          textAlign: 'center'
                        }} align="center" key={dayIndex}>
                          <Checkbox
                            checked={time.yn === 'N'}
                            onChange={() => handleServiceTimeChange(periodIndex, dayIndex)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TextField
              label={t('set.notes')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={4}
            />
            <Button onClick={handleSubmit} variant="contained" color="primary" fullWidth>
              {t('common:action.submit')}
            </Button>
            <br/><br/>
            <Button onClick={handleClose} variant="contained" color="secondary" fullWidth>
              {t('common:action.close')}
            </Button>
          </>
        )}
      </Box>
    </RootLayout>
  );
};

export default SetClinic;